import { TranslationWithDescriptionDTO } from "../../dto/TranslationWithDescriptionDTO";

export interface PublishDraft {
  translations: TranslationWithDescriptionDTO[];
  estimatedTimeInSecondsInput: string;
  savedAt: number;
}

const KEY_PREFIX = "publishDraft:";

/** Drafts are a safety net for a failed publish, not long-term storage. */
export const PUBLISH_DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export const publishDraftKey = (trainingId: string): string =>
  `${KEY_PREFIX}${trainingId}`;

// Same defensive shape as persistenceListenerMiddleware: storage can throw in
// private browsing modes, and a failure here must never break publishing.
const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignored
  }
};

const safeRemoveItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignored
  }
};

const isTranslation = (value: unknown): value is TranslationWithDescriptionDTO => {
  if (!value || typeof value !== "object") return false;
  const dto = value as Record<string, unknown>;
  return (
    typeof dto.languageId === "string" &&
    typeof dto.name === "string" &&
    typeof dto.description === "string"
  );
};

export const readPublishDraft = (
  trainingId: string,
  now: number = Date.now()
): PublishDraft | null => {
  if (!trainingId) return null;

  const raw = safeGetItem(publishDraftKey(trainingId));
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<PublishDraft>;

    if (
      !Array.isArray(parsed.translations) ||
      !parsed.translations.every(isTranslation) ||
      typeof parsed.estimatedTimeInSecondsInput !== "string" ||
      typeof parsed.savedAt !== "number"
    ) {
      clearPublishDraft(trainingId);
      return null;
    }

    if (now - parsed.savedAt > PUBLISH_DRAFT_MAX_AGE_MS) {
      clearPublishDraft(trainingId);
      return null;
    }

    return parsed as PublishDraft;
  } catch {
    // Corrupt entry - drop it rather than leave it to fail again next time.
    clearPublishDraft(trainingId);
    return null;
  }
};

/** Nothing typed yet means nothing worth keeping. */
export const hasDraftContent = (
  translations: TranslationWithDescriptionDTO[]
): boolean =>
  translations.some((t) => !!t.name?.trim() || !!t.description?.trim());

export const writePublishDraft = (
  trainingId: string,
  draft: Omit<PublishDraft, "savedAt">,
  now: number = Date.now()
): void => {
  if (!trainingId) return;

  safeSetItem(
    publishDraftKey(trainingId),
    JSON.stringify({ ...draft, savedAt: now })
  );
};

export const clearPublishDraft = (trainingId: string): void => {
  if (!trainingId) return;
  safeRemoveItem(publishDraftKey(trainingId));
};

/**
 * Draft values only ever fill gaps - anything the API returned wins, so a stale
 * draft can never overwrite what is actually stored on the course.
 */
export const mergeDraftIntoTranslations = (
  translations: TranslationWithDescriptionDTO[],
  draft: PublishDraft | null
): TranslationWithDescriptionDTO[] => {
  if (!draft) return translations;

  return translations.map((translation) => {
    const drafted = draft.translations.find(
      (d) => d.languageId === translation.languageId
    );
    if (!drafted) return translation;

    return {
      ...translation,
      name: translation.name?.trim() ? translation.name : drafted.name,
      description: translation.description?.trim()
        ? translation.description
        : drafted.description,
    };
  });
};
